import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Box } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import { useMediaQuery, useTheme } from '@mui/material';

// ✅ Images locales
import bg1 from '../assets/bg1.png';
import bg2 from '../assets/bg2.png';
import bg1_small from '../assets/bg1_small.png';
import bg2_small from '../assets/bg2_small.png';




function Home() {
  const { t } = useTranslation();
const theme = useTheme();
const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  // Choix dynamique des images selon la taille d’écran
const images = isSmallScreen ? [bg1_small, bg2_small] : [bg1, bg2];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCurtain, setShowCurtain] = useState(true);
  const [showImage, setShowImage] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);


  // 🟢 Étape 1 : Animation rideau au premier accès
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCurtain(false);
      setShowImage(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // 🖼️ Étape 2 : changement d'image auto (zoom-out)
  useEffect(() => {
    const interval = setInterval(() => {
      setFirstLoad(false);
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <>
    
      <Box sx={{ mt: 8, position: 'relative', overflow: 'hidden', minHeight: '100vh' }}> {/* Ajouté minHeight: '100vh' pour occuper toute la hauteur de l'écran immédiatement */}
        <Helmet>
          <title>SPEEDY Transport & Logistics</title>
        </Helmet>

        {/* ✅ Image de fond : animation différente pour la 1re apparition */}
        <AnimatePresence>
          {showImage && (
            <motion.div
              key={currentIndex}
              initial={
                firstLoad
                  ? { x: '-100%', opacity: 0 } // 👉 glisse de gauche pour la première
                  : { scale: 1.1, opacity: 0 } // 👉 effet zoom-out pour les suivantes
              }
              animate={{ x: 0, scale: 1, opacity: 1 }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100vh',
                backgroundImage: `url(${images[currentIndex]})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: 1,
              }}
            />
          )}
        </AnimatePresence>

        {/* ✅ Demi-cercle + texte */}
        {showImage && (
          <motion.div
            initial={{ x: -200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 2, delay: 0.8, ease: 'easeOut' }}
            style={{
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <Box
              sx={{
                width: { xs: '90%', sm: '90%', md: '35%' },
                height: { xs: '30vh',sm:'30vh', md: '100vh' },
                background: 'linear-gradient(90deg, #35E3C8 0%, #29C6D9 100%)',
                borderTopRightRadius: { xs: '20vh', md: '100vh' },
                borderBottomRightRadius: { xs: '20vh', md: '100vh' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: '#fff',
                textAlign: 'center',
                boxShadow: '5px 0 25px rgba(0,0,0,0.3)',
                px: { xs: 1,sm:2, md: 4 },
                mt:{xs:-60,sm:-60,md:0}
              }}
            >
              <Container maxWidth="lg">
               {/* ✅ Texte principal modernisé */}
<Typography
  variant="h3"
  sx={{
    fontWeight: 800,
    mb: 0.5,
    fontFamily: "'Poppins', sans-serif",
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    textShadow: '0px 2px 6px rgba(0,0,0,0.35)',
    fontSize: { xs: '1rem',sm:'1rem', md: '2rem' },
  }}
>
  {t('home.title')}
</Typography>

<Typography
  variant="h4"
  sx={{
    mb: 0.5,
    fontWeight: 500,
    color: '#f0f0f0',
    fontFamily: "'Poppins', sans-serif",
    lineHeight: 1.3,
    fontSize: { xs: '1rem',sm:'1rem', md: '1.6rem' },
    textShadow: '0px 1px 4px rgba(0,0,0,0.25)',
  }}
>
  {t('home.tit')}
</Typography>

<Typography
  variant="body1"
  sx={{
    mb: 0.5,
    fontWeight: 300,
    color: 'rgba(255,255,255,0.9)',
    fontSize: { xs: '0.8rem',sm:'0.8rem', md: '1.1rem' },
    lineHeight: 1.6,
    maxWidth: 500,
    mx: 'auto',
    textShadow: '0px 1px 3px rgba(0,0,0,0.2)',
  }}
>
  {t('home.subtitle')}
</Typography>


                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact"
                  sx={{
                    backgroundColor: '#007B8F',
                    px: 5,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    borderRadius: '30px',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#00B4C8',
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  {t('home.cta')}
                </Button>
              </Container>
            </Box>
          </motion.div>
        )}

        {/* 🟩 Rideau d'ouverture */}
        <AnimatePresence>
          {showCurtain && (
            <motion.div
              //initial={{ x: '-20%' }}
              animate={{ x: '0%' }}
              exit={{ x: '100%' }}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, #35E3C8 0%, #1e4044ff 100%)',
                zIndex: 10,
              }}
            />
          )}
        </AnimatePresence>
        {/* 💼 Section "Nos Services" centrée sur l’image */}
<motion.div
  initial={{ opacity: 0, y: 50 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 1, delay: 1 }}
  style={{
    position: 'absolute',
    top: '25%',
    left: '25%',
    transform: 'translate(-50%, -50%)',
    zIndex: 5,
    width: '70%',
  }}
>
  <Box
  sx={{
    borderRadius: '30px',
    p: { xs: 2, sm: 3, md: 4 },
    textAlign: { xs: 'center', md: 'left' },
    color: 'white',
    width: { xs: '100%', sm: '70%', md: '80%' },
    mx: 'auto',
  }}
>
  {currentIndex === 1 && ( // 🟢 n’afficher que quand l’image est bg2
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 1 }}
      style={{
        position: 'absolute',
        // ✅ Position différente selon la taille d'écran :
        top: 'auto',
        bottom: '10%',
        //left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        zIndex: 5,
      }}
    >
      <Box
        sx={{
          borderRadius: '30px',
          p: { xs: 1, sm: 1, md: 1 },
          textAlign: { xs: 'left',sm:'left', md: 'left' },
          color: 'white',
          width: { xs: '100%', sm: '90%', md: '80%' },
          mx: 'auto',
          position: 'relative',
          // ✅ Adaptation de la position selon le breakpoint :
          top: { xs: '530px',sm:'550px', md: '160px' }, // monte un peu en desktop
          //left: {  md: '-65%' }, // centrée sur mobile, décalée à gauche en md+
          transform: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
           justifyContent: { xs: 'center', sm: 'space-around', md: 'space-around' },
            alignItems: 'center',
            gap: { xs: 2, sm: 3, md: 4 },
flexDirection: { xs: 'row', sm: 'row' },

          }}
        >
          {[
            { key: 'home.transport', icon: <LocalShippingIcon sx={{ mr: 1.5, fontSize: { xs: '1rem', md: '3rem' } }} /> },
            { key: 'home.logistique', icon: <DirectionsCarFilledIcon sx={{ mr: 1.5, fontSize: { xs: '1rem', md: '3rem' } }} /> },
            { key: 'home.stockage', icon: <WarehouseIcon sx={{ mr: 1.5, fontSize: { xs: '1rem', md: '3rem' } }} /> },
            { key: 'home.suivi', icon: <TrackChangesIcon sx={{ mr: 1.5, fontSize: { xs: '1rem', md: '3rem' } }} /> },
            { key: 'home.flotte', icon: <SettingsInputComponentIcon sx={{ mr: 1.5, fontSize: { xs: '1rem', md: '3rem' } }} /> },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 + index * 0.15, duration: 0.6 }}
            >
              <Box
                sx={{
                  background: 'rgba(255, 255, 255, 0.7)',
                  px: { xs: 2, sm: 3, md: 4 },
                  py: { xs: 1, sm: 1.5 },
                  borderRadius: '40px',
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                  fontSize: { xs: '0.7rem', sm: '1rem', md: '1.2rem' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0d0b5eff',
                  '&:hover': { color: '#ffb703' },
                  minWidth: { xs: '60px', sm: '200px', md: '220px' },
                }}
              >
                {item.icon}
                {t(item.key)}
              </Box>
            </motion.div>
          ))}
        </Box>
      </Box>
    </motion.div>
  )}
</Box>


</motion.div>



      </Box>
    </>
  );
}

export default Home;
